/**
 * NsectionB6Page
 * B.6.避免重複計量（A6.4-FORM-METH-002 Section 6. Avoidance of double-counting）
 * Card + DataTable CRUD + Modal（段落+子段落）
 */

import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { validateSession } from '../utils/sessionValidator';
import { usePermission } from '../hooks/usePermission';
import { useProject } from '../contexts/ProjectContext';
import nsectionB6Service, { NsectionB6Detail, DoubleCountingItem, SubParagraphItem } from '../services/nsectionB6Service';
import RichTextEditor from '../components/RichTextEditor';
import FunctionPageHeader from '../components/FunctionPageHeader';
import { useToast } from '../contexts/ToastContext';
import '../styles/DataTable.css';

const SUB_LABELS = 'abcdefghijklmnopqrstuvwxyz';

const NsectionB6Page: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isZh = i18n.language === 'zh-TW';
  const { hasPermission, loading: permissionLoading } = usePermission();
  const { selectedProposalId } = useProject();
  const toast = useToast();
  const hasInitialized = useRef(false);

  const [data, setData] = useState<NsectionB6Detail | null>(null);
  const [conditions, setConditions] = useState<DoubleCountingItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Modal
  const [showModal, setShowModal] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [modalParagraph, setModalParagraph] = useState('');
  const [modalSubParagraphs, setModalSubParagraphs] = useState<SubParagraphItem[]>([]);

  useEffect(() => {
    if (!hasInitialized.current && !permissionLoading) {
      validateSession('NsectionB6Page');
      hasInitialized.current = true;
    }
  }, [permissionLoading]);

  useEffect(() => {
    if (!selectedProposalId) {
      setData(null);
      setConditions([]);
      return;
    }
    const load = async () => {
      try {
        setLoading(true); setError(null);
        const result = await nsectionB6Service.getByProposal(selectedProposalId);
        if (result) {
          setData(result);
          setConditions(result.double_counting_avoidance || []);
        } else {
          setData(null);
          setConditions([]);
        }
      } catch (err: any) {
        if (err.response?.status !== 404) {
          setError(err.response?.data?.detail || (isZh ? '載入資料失敗' : 'Failed to load data'));
        }
      } finally { setLoading(false); }
    };
    load();
  }, [selectedProposalId, isZh]);

  // Modal 操作
  const openModal = (index?: number) => {
    if (index !== undefined) {
      setEditingIndex(index);
      setModalParagraph(conditions[index].paragraph);
      setModalSubParagraphs([...conditions[index].sub_paragraphs.map(sp => ({ ...sp }))]);
    } else {
      setEditingIndex(null);
      setModalParagraph('');
      setModalSubParagraphs([]);
    }
    setShowModal(true);
  };

  const handleModalConfirm = () => {
    const stripped = modalParagraph.replace(/<[^>]+>/g, '').trim();
    if (!stripped) {
      toast.warning(isZh ? '請輸入段落內容' : 'Please enter paragraph content');
      return;
    }
    const updated = [...conditions];
    const item: DoubleCountingItem = {
      paragraph: modalParagraph,
      sub_paragraphs: modalSubParagraphs.filter(sp => sp.content.replace(/<[^>]+>/g, '').trim()),
    };
    if (editingIndex !== null) {
      updated[editingIndex] = item;
    } else {
      updated.push(item);
    }
    setConditions(updated);
    setShowModal(false);
  };

  const handleDelete = async (index: number) => {
    const ok = await toast.confirm({
      title: isZh ? '刪除確認' : 'Delete Confirmation',
      message: isZh ? '確定要刪除此段落嗎？' : 'Are you sure you want to delete this paragraph?',
      confirmText: isZh ? '刪除' : 'Delete',
      severity: 'error',
    });
    if (!ok) return;
    const updated = conditions.filter((_, i) => i !== index);
    setConditions(updated);
  };

  // 子段落操作
  const addSubParagraph = () => {
    setModalSubParagraphs(prev => [...prev, { content: '' }]);
  };

  const updateSubParagraph = (idx: number, content: string) => {
    setModalSubParagraphs(prev => {
      if (idx < 0 || idx >= prev.length) return prev;
      const updated = [...prev];
      updated[idx] = { content };
      return updated;
    });
  };

  const removeSubParagraph = (idx: number) => {
    setModalSubParagraphs(prev => prev.filter((_, i) => i !== idx));
  };

  // 儲存
  const handleSave = async () => {
    if (!selectedProposalId) return;
    try {
      setLoading(true);
      const result = await nsectionB6Service.update(selectedProposalId, { double_counting_avoidance: conditions });
      setData(result);
      toast.success(isZh ? '儲存成功' : 'Saved successfully');
    } catch (err: any) {
      toast.error(err.response?.data?.detail || (isZh ? '儲存失敗' : 'Save failed'));
    } finally { setLoading(false); }
  };

  // 渲染段落內容（含子段落）
  const renderConditionContent = (item: DoubleCountingItem) => {
    return (
      <div>
        <div dangerouslySetInnerHTML={{ __html: item.paragraph || '-' }} />
        {item.sub_paragraphs && item.sub_paragraphs.length > 0 && (
          <div style={{ paddingLeft: '24px', marginTop: '8px' }}>
            {item.sub_paragraphs.map((sp, spIdx) => (
              <div key={spIdx} style={{ display: 'flex', marginBottom: '4px' }}>
                <span style={{ minWidth: '28px', color: '#666', fontWeight: 600 }}>({SUB_LABELS[spIdx] || spIdx + 1})</span>
                <div dangerouslySetInnerHTML={{ __html: sp.content || '-' }} />
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  if (permissionLoading) return <div className="page-container"><div className="loading">{t('common.loading')}</div></div>;
  if (!hasPermission('nsection_b6', 'read')) return <div className="page-container"><div className="error-message">{t('common.noPermission')}</div></div>;
  const canUpdate = hasPermission('nsection_b6', 'update');

  return (
    <div className="page-container">
      <div className="page-header">
        <FunctionPageHeader funcCode="nsection_b6" />
      </div>

      {error && <div className="error-message" style={{ marginBottom: '16px' }}>{error}</div>}

      {!selectedProposalId ? (
        <div style={{ padding: '48px', textAlign: 'center', color: '#999', fontSize: '16px' }}>
          {isZh ? '請先在上方選擇方法論提案' : 'Please select a methodology proposal from the top bar'}
        </div>
      ) : loading && !data ? (
        <div className="loading">{t('common.loading')}</div>
      ) : (
        <div>
          <div style={{
            width: '100%', background: '#fff', borderRadius: '10px',
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)', border: '1px solid #e8e8e8', padding: '28px 32px',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', paddingBottom: '12px', borderBottom: '1px solid #f0f0f0' }}>
              <div style={{ fontSize: '16px', fontWeight: 700, color: '#333' }}>
                {isZh ? '避免重複計量段落' : 'Avoidance of Double-counting'}
              </div>
              {canUpdate && (
                <button className="btn-primary" onClick={() => openModal()} style={{ fontSize: '13px', padding: '6px 16px' }}>
                  {t('common.create')}
                </button>
              )}
            </div>

            {conditions.length === 0 ? (
              <div style={{ padding: '32px', textAlign: 'center', color: '#999', fontSize: '14px' }}>
                {isZh ? '尚無段落，請點擊「新增」按鈕' : 'No paragraphs yet. Click "Create" to add one.'}
              </div>
            ) : (
              <div className="data-table-container">
                <table className="data-table">
                  <thead className="table-header-dark-green">
                    <tr>
                      <th style={{ width: '5%' }}>#</th>
                      <th style={{ width: '80%' }}>{isZh ? '段落內容' : 'Paragraph Content'}</th>
                      <th style={{ width: '15%' }}>{t('common.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {conditions.map((item, idx) => (
                      <tr key={idx}>
                        <td>{idx + 1}</td>
                        <td>{renderConditionContent(item)}</td>
                        <td className="actions">
                          {canUpdate && (
                            <>
                              <button className="btn-edit" onClick={() => openModal(idx)} style={{ marginRight: '8px' }}>
                                {t('common.edit')}
                              </button>
                              <button className="btn-delete" onClick={() => handleDelete(idx)}>
                                {t('common.delete')}
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {canUpdate && (
              <div style={{ display: 'flex', justifyContent: 'center', paddingTop: '20px', borderTop: '1px solid #f0f0f0', marginTop: '16px' }}>
                <button className="btn-primary" onClick={handleSave} disabled={loading} style={{ padding: '8px 32px' }}>
                  {loading ? (isZh ? '儲存中...' : 'Saving...') : t('common.save')}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px' }}>
            <div className="modal-header">
              <h2>{editingIndex !== null ? (isZh ? '編輯段落' : 'Edit Paragraph') : (isZh ? '新增段落' : 'Add Paragraph')}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>✕</button>
            </div>
            <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, fontSize: '14px' }}>
                  {isZh ? '段落內容' : 'Paragraph Content'} *
                </label>
                <RichTextEditor
                  value={modalParagraph}
                  onChange={setModalParagraph}
                  placeholder={isZh ? '請輸入段落內容...' : 'Enter paragraph content...'}
                  minHeight="120px"
                />
              </div>

              <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <label style={{ fontWeight: 600, fontSize: '14px' }}>
                    {isZh ? '子段落' : 'Sub-paragraphs'}
                  </label>
                  <button
                    className="btn-primary"
                    onClick={addSubParagraph}
                    style={{ fontSize: '12px', padding: '4px 12px' }}
                  >
                    {isZh ? '新增子段落' : 'Add Sub-paragraph'}
                  </button>
                </div>

                {modalSubParagraphs.length === 0 ? (
                  <div style={{ padding: '16px', textAlign: 'center', color: '#999', fontSize: '13px', background: '#fafafa', borderRadius: '6px' }}>
                    {isZh ? '無子段落（可選）' : 'No sub-paragraphs (optional)'}
                  </div>
                ) : (
                  modalSubParagraphs.map((sp, spIdx) => (
                    <div key={spIdx} style={{ marginBottom: '12px', padding: '12px', background: '#fafafa', borderRadius: '6px', border: '1px solid #eee' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                        <span style={{ fontWeight: 600, fontSize: '13px', color: '#666' }}>({SUB_LABELS[spIdx] || spIdx + 1})</span>
                        <button
                          type="button"
                          className="btn-delete"
                          onClick={() => removeSubParagraph(spIdx)}
                          style={{ fontSize: '12px', padding: '2px 8px' }}
                        >
                          {t('common.delete')}
                        </button>
                      </div>
                      <RichTextEditor
                        value={sp.content}
                        onChange={(val) => updateSubParagraph(spIdx, val)}
                        placeholder={isZh ? '請輸入子段落內容...' : 'Enter sub-paragraph content...'}
                        minHeight="80px"
                      />
                    </div>
                  ))
                )}
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowModal(false)}>{t('common.cancel')}</button>
              <button className="btn-primary" onClick={handleModalConfirm}>{t('common.save')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NsectionB6Page;
